// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Message, MessageBox, Loading } from '@isyscore/base-ui';
import { isObject, isString } from '@isyscore/utils/type';
import { objectMerge } from '@isyscore/utils/object';

// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
const toMessage = (message: any) => String(message && message.message ? message.message : message);

/**
 * 成功消息
 * @param message
 * @returns {void}
 */
export function showSuccess(message: any): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-return
    return Message({
        type: 'success',
        showClose: true,
        message: toMessage(message)
    });
}

/**
 * 错误消息
 * @param message
 * @returns {void}
 */
export function showError(message: any): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-return
    return Message({
        type: 'error',
        showClose: true,
        message: toMessage(message)
    });
}

/**
 * 警告消息
 * @param message
 * @returns {void}
 */
export function showWarning(message: any) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-return
    return Message({
        type: 'warning',
        showClose: true,
        message: toMessage(message)
    });
}

interface MessageBoxOptions {
    /**
     * MessageBox 标题
     */
    title: string;

    /**
     * MessageBox 消息正文内容
     */
    message: string;

    /**
     * 是否将 message 属性作为 HTML 片段处理
     */
    dangerouslyUseHTMLString: boolean;

    /**
     * 消息类型，用于显示图标
     */
    type: 'success' | 'info' | 'warning' | 'error';

    /**
     * MessageBox 是否显示右上角关闭按钮
     */
    showClose: boolean;

    /**
     * 是否将取消（点击取消按钮）与关闭（点击关闭按钮或遮罩层、按下 ESC 键）进行区分
     */
    distinguishCancelAndClose: boolean;

    /**
     * 是否在 MessageBox 出现时将 body 滚动锁定
     */
    lockScroll: boolean;

    /**
     * 是否显示取消按钮
     */
    showCancelButton: boolean;

    /**
     * 是否显示确认按钮
     */
    showConfirmButton: boolean;

    /**
     * 是否使用圆角按钮
     */
    roundButton: boolean;

    /**
     * 取消按钮的文本内容
     */
    cancelButtonText: string;

    /**
     * 确认按钮的文本内容
     */
    confirmButtonText: string;

    /**
     * 是否可通过点击遮罩关闭 MessageBox
     */
    closeOnClickModal: boolean;

    /**
     * 是否可通过按下 ESC 键关闭 MessageBox
     */
    closeOnPressEscape: boolean;

    /**
     * 是否在 hashchange 时关闭 MessageBox
     */
    closeOnHashChange: boolean;

    /**
     * 输入框的占位符
     */
    inputPlaceholder: string;

    /**
     * 输入框的类型
     */
    inputType: 'text' | string;

    /**
     * 输入框的初始文本
     */
    inputValue: string;

    /**
     * 输入框的校验表达式
     */
    inputPattern: RegExp;

    /**
     * 输入框的校验函数。可以返回布尔值或字符串，若返回一个字符串, 则返回结果会被赋值给 inputErrorMessage
     */
    inputValidator: () => boolean | string;

    /**
     * 校验未通过时的提示文本
     */
    inputErrorMessage: string;

    /**
     * 是否居中布局
     */
    center: boolean;
}

const messageBoxDefaults: Partial<MessageBoxOptions> = {
    showCancelButton: true,
    showConfirmButton: true,
    cancelButtonText: '取消',
    confirmButtonText: '确定',
    distinguishCancelAndClose: false,
    closeOnClickModal: false,
    closeOnPressEscape: false,
    closeOnHashChange: false
};

/**
 * 提醒消息
 * @param message
 * @param [title]
 * @param [options]
 * @returns {Promise<void>}
 */
export async function openAlert(
    message: string,
    title?: string | Partial<MessageBoxOptions>,
    options?: Partial<MessageBoxOptions>
): Promise<void> {
    const finalOptions: Partial<MessageBoxOptions> = {
        ...messageBoxDefaults,
        type: 'info',
        title: '提醒',
        showCancelButton: false
    };

    if (isObject(title)) {
        objectMerge(finalOptions, title);
    } else if (isString(title)) {
        objectMerge(finalOptions, { title }, options);
    }

    try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-member-access
        await MessageBox.alert(toMessage(message), finalOptions);
    } catch (err) {
        // ignore
    }
}

/**
 * 二次确认
 * @param message
 * @param [title]
 * @param [options]
 * @returns {Promise<*>}
 */
export async function openConfirm(
    message: string,
    title?: string | Partial<MessageBoxOptions>,
    options?: Partial<MessageBoxOptions>
): Promise<void> {
    const finalOptions: Partial<MessageBoxOptions> = { ...messageBoxDefaults, type: 'warning', title: '请确认' };

    if (isObject(title)) {
        objectMerge(finalOptions, title);
    } else if (isString(title)) {
        objectMerge(finalOptions, { title }, options);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-member-access
    return await MessageBox.confirm(toMessage(message), finalOptions);
}

/**
 * 确认输入
 * @param message
 * @param [title]
 * @param [options]
 * @returns {Promise<*>}
 */
export async function openPrompt(
    message: string,
    title?: string | Partial<MessageBoxOptions>,
    options?: Partial<MessageBoxOptions>
): Promise<string> {
    const finalOptions: Partial<MessageBoxOptions> = { ...messageBoxDefaults, title: '请输入' };

    if (isObject(title)) {
        objectMerge(finalOptions, title);
    } else if (isString(title)) {
        objectMerge(finalOptions, { title }, options);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
    const { value } = await MessageBox.prompt(toMessage(message), finalOptions);
    return (value as string) || '';
}
